import { z } from 'zod';
import {
  BullMQPayloadBaseSchema,
  JobRedisStateBaseSchema,
  RequestParamsBaseSchema,
} from './base.js';
import { JurisdictionSchema, MAX_JURISDICTIONS } from '../enums/index.js';

export const ResearchOnlyPayloadSchema = BullMQPayloadBaseSchema.passthrough();
export type ResearchOnlyPayload = z.infer<typeof ResearchOnlyPayloadSchema>;

const ResearchOnlyRequestParamsSchema = RequestParamsBaseSchema.extend({
  actId: z.string().regex(/^act_[0-9a-f]{32}$/).optional(),
  selectedDbCaseId: z.string().uuid().optional(),
  useParallelGrokAggregation: z.boolean().optional(),
  skipJurisprudenceResearch: z
    .union([z.boolean(), z.literal('true'), z.literal('false')])
    .optional()
    .describe(
      "User-driven JP skip (lawyer unchecked all jurisdiction checkboxes in the add-in). " +
      "Accepts boolean OR string literal 'true'/'false' (FormData stringifies values).",
    ),
  jurisdictions: z
    .array(JurisdictionSchema)
    .max(MAX_JURISDICTIONS)
    .optional()
    .describe(
      "User-selected JP court override (e.g. ['CC', 'CA'] judicial OR " +
      "['CE', 'CAA', 'TA'] administratif). When present, bypasses auto-resolution " +
      "from specialisation in EnhancedJurisprudenceController. Validated against " +
      "the full JurisdictionSchema — invalid codes fail Zod parse and the job is " +
      "rejected. Admin codes added 2026-06-20 (handoff Droit Public).",
    ),
  // ── Conclusions en réplique (fork `mode:'riposte'`) ──
  // Bascule le legal-planner du pipeline research-only : au lieu de générer des
  // moyens depuis le dossier, il CLASSE les moyens adverses (repris/enrichi/
  // nouveau) vs NOTRE écriture n°1. Absent ⇒ 'standard' ⇒ zéro régression sur
  // tous les actes existants. Voir
  // `docs/features/act-generation/plans/2026-07-03-conclusions-replique-simple-fork.html`.
  mode: z.enum(['standard', 'riposte']).optional(),
  // Id de la pièce du dossier = NOTRE écriture n°1 (celle à laquelle on réplique).
  // Requis quand `mode:'riposte'` (garde côté processor) ; son texte OCR intégral
  // est chargé depuis `case_document_embeddings` et sert (a) d'input au classifieur
  // riposte, (b) de source de reprise verbatim à la génération de l'acte
  // (`{{ownPreviousPleadings}}`). L'écriture ADVERSE, elle, arrive déjà via les
  // champs `adverse_pleadings_*` du FileProcessing standard.
  ourPriorPleadingDocId: z.string().uuid().optional(),
  // pipelineVersion supprimé 2026-05-11 avec le pipeline V2 expérimental.
  // Seul V1-bis tourne en production. Voir
  // `docs/features/act-generation/archive/plan-detaille-v2-experimental.md`.
}).passthrough();

export const ResearchOnlyJobDataSchema = JobRedisStateBaseSchema
  .extend({
    request_params: ResearchOnlyRequestParamsSchema,
    // `.nullable().optional()` because `JobManager.cleanupJobFiles` writes
    // `file_data: null` explicitly (not `delete`) to free Redis memory after
    // the worker has loaded files — see `services/job_manager.ts:472`. Zod
    // `.optional()` alone accepts `undefined` but rejects `null`, which was
    // the root cause of `research_only_jobdata_schema_drift` on BullMQ
    // retries / crash-safe resumes. The processor already handles the
    // cleaned branch via `files_loaded_by_worker: true` (JOB_ALREADY_FAILED /
    // JOB_ALREADY_PROCESSING — generate_document_plan_processor.ts:222).
    // Mirrors the same fix on generate-document.ts (Path 3).
    file_data: z.record(z.string(), z.unknown()).nullable().optional(),
    files_loaded_by_worker: z.boolean().optional(),
  })
  .passthrough();
export type ResearchOnlyJobData = z.infer<typeof ResearchOnlyJobDataSchema>;
